import React, { useState } from 'react';
import {
  Settings,
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  Eye,
  EyeOff,
  Move,
  Database,
  Mail,
  Zap,
  Shield
} from 'lucide-react';
import { DynamicField, FormConfiguration, EmailTemplate, CRMIntegration } from '../../types';
import { StandardButton } from '../../components/standard';

interface SystemConfigurationProps {
  currentUser?: any;
}

const SystemConfiguration: React.FC<SystemConfigurationProps> = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('fields');
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState<DynamicField | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);

  // Mock data for dynamic fields
  const [dynamicFields, setDynamicFields] = useState<DynamicField[]>([
    {
      id: 'field_1',
      name: 'secondary_contact',
      label: 'Secondary Contact',
      type: 'text',
      required: false,
      placeholder: 'Enter secondary contact name',
      department: 'all',
      section: 'contact_details',
      order: 1,
      isActive: true,
      createdBy: 'admin@company.com',
      createdAt: '2025-01-15T10:00:00Z',
      updatedAt: '2025-01-15T10:00:00Z'
    },
    {
      id: 'field_2',
      name: 'preferred_contact_method',
      label: 'Preferred Contact Method',
      type: 'select',
      required: true,
      options: ['Email', 'Phone', 'Text', 'Mail'],
      department: 'credit_management',
      section: 'client_info',
      order: 2,
      isActive: true,
      createdBy: 'admin@company.com',
      createdAt: '2025-01-15T10:00:00Z',
      updatedAt: '2025-01-15T10:00:00Z'
    }
  ]);

  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([
    {
      id: 'template_1',
      name: 'Welcome Email - Credit Repair',
      subject: 'Welcome to {{company_name}} Credit Repair Services',
      body: 'Dear {{client_name}},\n\nWelcome to our credit repair program! We are excited to help you improve your credit score.\n\nYour current score: {{current_score}}\nTarget score: {{target_score}}\n\nBest regards,\n{{agent_name}}',
      department: 'credit_management',
      category: 'welcome',
      variables: [
        { name: 'client_name', label: 'Client Name', type: 'client_field' },
        { name: 'current_score', label: 'Current Score', type: 'client_field' },
        { name: 'target_score', label: 'Target Score', type: 'client_field' },
        { name: 'agent_name', label: 'Agent Name', type: 'system_field' },
        { name: 'company_name', label: 'Company Name', type: 'system_field' }
      ],
      isActive: true,
      createdBy: 'admin@company.com',
      createdAt: '2025-01-15T10:00:00Z',
      updatedAt: '2025-01-15T10:00:00Z'
    }
  ]);

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Select Dropdown' },
    { value: 'multiselect', label: 'Multi-Select' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'url', label: 'URL' }
  ];

  const departments = [
    { value: 'all', label: 'All Departments' },
    { value: 'credit_management', label: 'Credit Management' },
    { value: 'cybersweep', label: 'CyberSweep' },
    { value: 'credit_block', label: 'Credit Block' }
  ];

  const sections = [
    { value: 'client_info', label: 'Client Information' },
    { value: 'contact_details', label: 'Contact Details' },
    { value: 'products', label: 'Products' },
    { value: 'custom', label: 'Custom Section' }
  ];

  const tabs = [
    { id: 'fields', label: 'Dynamic Fields', icon: Database, count: dynamicFields.length },
    { id: 'forms', label: 'Form Configurations', icon: Settings, count: 3 },
    { id: 'emails', label: 'Email Templates', icon: Mail, count: emailTemplates.length },
    { id: 'crm', label: 'CRM Integration', icon: Zap, count: 1 }
  ];

  const handleSaveField = (fieldData: any) => {
    if (editingField) {
      setDynamicFields(fields =>
        fields.map(f => f.id === editingField.id ? { ...fieldData, id: editingField.id } : f)
      );
    } else {
      const newField: DynamicField = {
        ...fieldData,
        id: `field_${Date.now()}`,
        createdBy: currentUser?.email || 'admin@company.com',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setDynamicFields(fields => [...fields, newField]);
    }
    setShowModal(false);
    setEditingField(null);
  };

  const renderFieldsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Dynamic Fields Management</h3>
        <StandardButton
          variant="primary"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => {
            setEditingField(null);
            setShowModal(true);
          }}
        >
          Add Field
        </StandardButton>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Field</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Department</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Section</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
            {dynamicFields.map((field) => (
              <tr key={field.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{field.label}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{field.name}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                    {field.type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {departments.find(d => d.value === field.department)?.label}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {sections.find(s => s.value === field.section)?.label}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    field.isActive
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                  }`}>
                    {field.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setEditingField(field);
                        setShowModal(true);
                      }}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        setDynamicFields(fields =>
                          fields.map(f =>
                            f.id === field.id ? { ...f, isActive: !f.isActive } : f
                          )
                        );
                      }}
                      className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300"
                    >
                      {field.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderEmailTemplatesTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Email Templates</h3>
        <StandardButton
          variant="primary"
          leftIcon={<Plus className="h-4 w-4" />}
        >
          Add Template
        </StandardButton>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {emailTemplates.map((template) => (
          <div key={template.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <Mail className="h-8 w-8 text-blue-600" />
              <span className={`px-2 py-1 text-xs rounded-full ${
                template.isActive
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
              }`}>
                {template.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{template.name}</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              <strong>Subject:</strong> {template.subject}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              <strong>Department:</strong> {departments.find(d => d.value === template.department)?.label}
            </p>
            <div className="flex items-center justify-between">
              <span className="px-2 py-1 text-xs rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                {template.category}
              </span>
              <div className="flex items-center space-x-2">
                <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                  <Edit3 className="h-4 w-4" />
                </button>
                <button className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300">
                  <Eye className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCRMTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">CRM Integration</h3>
        <StandardButton
          variant="primary"
          leftIcon={<Plus className="h-4 w-4" />}
        >
          Add Integration
        </StandardButton>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Zap className="h-8 w-8 text-blue-600" />
            <span className="px-2 py-1 text-xs rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
              Connected
            </span>
          </div>
          <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Salesforce</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Sync clients, emails, and activities with Salesforce CRM
          </p>
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Last Sync:</span>
              <span className="text-gray-900 dark:text-gray-100">2 hours ago</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Auto Sync:</span>
              <span className="text-green-600 dark:text-green-400">Enabled</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium">
              Configure
            </button>
            <div className="flex items-center space-x-2">
              <button className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300">
                <Settings className="h-4 w-4" />
              </button>
              <button className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300">
                <Zap className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Zap className="h-8 w-8 text-orange-600" />
            <span className="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
              Not Connected
            </span>
          </div>
          <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Zoho CRM</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Connect with Zoho CRM to sync your data
          </p>
          <div className="flex items-center justify-between">
            <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium">
              Connect
            </button>
            <button className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300">
              <Settings className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">System Configuration</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Manage dynamic fields, email templates, and integrations</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="border-b border-gray-200 dark:border-gray-600">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                    {tab.count > 0 && (
                      <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 py-1 px-2 rounded-full text-xs">
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'fields' && renderFieldsTab()}
            {activeTab === 'forms' && <div>Form Configurations - Coming Soon</div>}
            {activeTab === 'emails' && renderEmailTemplatesTab()}
            {activeTab === 'crm' && renderCRMTab()}
          </div>
        </div>
      </div>

      {/* Field Editor Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {editingField ? 'Edit Field' : 'Add New Field'}
              </h3>
            </div>
            <div className="px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Field Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="field_name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Display Label
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Field Label"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Field Type
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    {fieldTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Department
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    {departments.map(dept => (
                      <option key={dept.value} value={dept.value}>{dept.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 flex justify-end space-x-3">
              <StandardButton
                variant="secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </StandardButton>
              <StandardButton variant="primary">
                Save Field
              </StandardButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemConfiguration;